
import React, { createContext, useContext, useReducer, useEffect, ReactNode, useState, useRef } from 'react'
import { lumi } from '../lib/lumi'

export interface CartItem {
  _id: string
  name: string
  nameEn?: string
  price: number
  image: string
  category: string
  quantity: number
  maxQuantity?: number
  specifications?: {
    roastLevel?: string
    grindSize?: string
    packaging?: string
  }
}

interface CartState {
  items: CartItem[]
  total: number
  itemCount: number
}

type CartAction =
  | { type: 'ADD_ITEM'; payload: Omit<CartItem, 'quantity'> & { quantity?: number } }
  | { type: 'REMOVE_ITEM'; payload: string }
  | { type: 'UPDATE_QUANTITY'; payload: { id: string; quantity: number } }
  | { type: 'CLEAR_CART' }
  | { type: 'LOAD_CART'; payload: CartItem[] }

interface CartContextType extends CartState {
  addItem: (item: Omit<CartItem, 'quantity'> & { quantity?: number }) => Promise<void>
  removeItem: (id: string) => Promise<void>
  updateQuantity: (id: string, quantity: number) => Promise<void>
  clearCart: () => Promise<void>
}

const CartContext = createContext<CartContextType | undefined>(undefined)

const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case 'ADD_ITEM': {
      const existingItem = state.items.find(item => item._id === action.payload._id)
      
      if (existingItem) {
        const newQuantity = existingItem.quantity + (action.payload.quantity || 1)
        const maxQuantity = existingItem.maxQuantity || 999
        const updatedItems = state.items.map(item =>
          item._id === action.payload._id
            ? { ...item, quantity: Math.min(newQuantity, maxQuantity) }
            : item
        )
        
        return {
          ...state,
          items: updatedItems,
          total: updatedItems.reduce((sum, item) => sum + item.price * item.quantity, 0),
          itemCount: updatedItems.reduce((sum, item) => sum + item.quantity, 0)
        }
      }
      
      const newItem: CartItem = {
        ...action.payload,
        quantity: action.payload.quantity || 1
      }
      
      const newItems = [...state.items, newItem]
      
      return {
        ...state,
        items: newItems,
        total: newItems.reduce((sum, item) => sum + item.price * item.quantity, 0),
        itemCount: newItems.reduce((sum, item) => sum + item.quantity, 0)
      }
    }
    
    case 'REMOVE_ITEM': {
      const newItems = state.items.filter(item => item._id !== action.payload)
      
      return {
        ...state,
        items: newItems,
        total: newItems.reduce((sum, item) => sum + item.price * item.quantity, 0),
        itemCount: newItems.reduce((sum, item) => sum + item.quantity, 0)
      }
    }
    
    case 'UPDATE_QUANTITY': {
      if (action.payload.quantity <= 0) {
        return cartReducer(state, { type: 'REMOVE_ITEM', payload: action.payload.id })
      }
      
      const updatedItems = state.items.map(item =>
        item._id === action.payload.id
          ? { ...item, quantity: Math.min(action.payload.quantity, item.maxQuantity || 999) }
          : item
      )
      
      return {
        ...state,
        items: updatedItems,
        total: updatedItems.reduce((sum, item) => sum + item.price * item.quantity, 0),
        itemCount: updatedItems.reduce((sum, item) => sum + item.quantity, 0)
      }
    }
    
    case 'CLEAR_CART':
      return {
        items: [],
        total: 0,
        itemCount: 0
      }
    
    case 'LOAD_CART': {
      const items = action.payload
      return {
        items,
        total: items.reduce((sum, item) => sum + item.price * item.quantity, 0),
        itemCount: items.reduce((sum, item) => sum + item.quantity, 0)
      }
    }
    
    default:
      return state
  }
}

interface CartProviderProps {
  children: ReactNode
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, {
    items: [],
    total: 0,
    itemCount: 0
  })
  const [isLoading, setIsLoading] = useState(true)
  const [currentUserId, setCurrentUserId] = useState<string | null>(null)
  const [hasInitialized, setHasInitialized] = useState(false)
  const isInitialLoadRef = useRef(false)

  // 合併購物車：相同商品數量相加，不同商品都保留
  const mergeCartItems = (dbItems: CartItem[], localItems: CartItem[]): CartItem[] => {
    const merged = [...dbItems]
    
    localItems.forEach(localItem => {
      const existingIndex = merged.findIndex(item => item._id === localItem._id)
      if (existingIndex !== -1) {
        // 相同商品 → 數量相加（不超過 maxQuantity）
        const existing = merged[existingIndex]
        const maxQty = existing.maxQuantity || 999
        merged[existingIndex] = {
          ...existing,
          quantity: Math.min(existing.quantity + localItem.quantity, maxQty)
        }
      } else {
        // 不同商品 → 新增
        merged.push(localItem)
      }
    })
    
    return merged
  }

  // 監聽登入狀態並載入購物車
  useEffect(() => {
    const initializeCart = async () => {
      try {
        // 檢查登入狀態
        const user = lumi.auth.user
        const userId = user?.userId

        if (userId) {
          setCurrentUserId(userId)
          
          // 從數據庫讀取會員購物車
          const { list: dbCartItems } = await lumi.entities.cart_items_v2.list({
            filter: { userId },
            sort: { createdAt: 1 }
          })

          if (dbCartItems.length > 0) {
            // 資料庫有資料 → 轉換格式並載入
            const dbCart: CartItem[] = dbCartItems.map((dbItem: any) => ({
              _id: dbItem.productId,
              name: dbItem.productName,
              nameEn: dbItem.productNameEn,
              price: dbItem.price,
              image: dbItem.image,
              category: dbItem.category,
              quantity: dbItem.quantity,
              maxQuantity: dbItem.maxQuantity,
              specifications: dbItem.specifications
            }))
            
            // 標記為初始載入，避免觸發自動同步
            isInitialLoadRef.current = true
            dispatch({ type: 'LOAD_CART', payload: dbCart })
            
            // 刪除 localStorage（資料庫優先）
            localStorage.removeItem('ongu-cart')
          } else {
            // 數據庫為空 → 檢查 localStorage 是否有訪客購物車
            const savedCart = localStorage.getItem('ongu-cart')
            const localCartItems: CartItem[] = savedCart ? JSON.parse(savedCart) : []
            
            if (localCartItems.length > 0) {
              // 有訪客購物車 → 上傳到數據庫
              await lumi.entities.cart_items_v2.createMany(
                localCartItems.map(item => ({
                  userId,
                  productId: item._id,
                  productName: item.name,
                  productNameEn: item.nameEn || '',
                  price: item.price,
                  image: item.image,
                  category: item.category,
                  quantity: item.quantity,
                  maxQuantity: item.maxQuantity || 999,
                  specifications: item.specifications || {},
                  creator: userId,
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString()
                }))
              )
              
              // 刪除 localStorage
              localStorage.removeItem('ongu-cart')
            }
            
            // 標記為初始載入
            isInitialLoadRef.current = true
            dispatch({ type: 'LOAD_CART', payload: localCartItems })
          }
        } else {
          // 未登入，只載入 localStorage
          const savedCart = localStorage.getItem('ongu-cart')
          const localCartItems: CartItem[] = savedCart ? JSON.parse(savedCart) : []
          isInitialLoadRef.current = true
          dispatch({ type: 'LOAD_CART', payload: localCartItems })
        }
      } catch (error) {
        console.error('Failed to initialize cart:', error)
        // 失敗時仍嘗試從 localStorage 載入
        const savedCart = localStorage.getItem('ongu-cart')
        if (savedCart) {
          try {
            const cartItems = JSON.parse(savedCart)
            dispatch({ type: 'LOAD_CART', payload: cartItems })
          } catch (e) {
            console.error('Failed to parse localStorage cart:', e)
          }
        }
      } finally {
        setIsLoading(false)
        setHasInitialized(true)
        // 初始載入完成後，重置標記
        setTimeout(() => {
          isInitialLoadRef.current = false
        }, 100)
      }
    }

    initializeCart()

    // 監聽登入狀態變化
    const unsubscribe = lumi.auth.onAuthChange(({ user }) => {
      const newUserId = user?.userId || null
      if (newUserId !== currentUserId) {
        setCurrentUserId(newUserId)
        // 登入狀態改變，重新初始化
        initializeCart()
      }
    })

    return unsubscribe
  }, [currentUserId])

  // 保存到 localStorage（僅未登入時）
  useEffect(() => {
    if (!isLoading && !currentUserId) {
      localStorage.setItem('ongu-cart', JSON.stringify(state.items))
    }
  }, [state.items, isLoading, currentUserId])

  const addItem = async (item: Omit<CartItem, 'quantity'> & { quantity?: number }) => {
    dispatch({ type: 'ADD_ITEM', payload: item })
    
    // 登入狀態下同步到資料庫
    if (currentUserId) {
      try {
        const { list: existingItems } = await lumi.entities.cart_items_v2.list({
          filter: { userId: currentUserId, productId: item._id }
        })
        
        if (existingItems.length > 0) {
          // 商品已存在，更新數量
          const existingItem = existingItems[0]
          const newQuantity = existingItem.quantity + (item.quantity || 1)
          await lumi.entities.cart_items_v2.update(existingItem._id, {
            quantity: Math.min(newQuantity, item.maxQuantity || 999),
            updatedAt: new Date().toISOString()
          })
        } else {
          // 商品不存在，新增記錄
          await lumi.entities.cart_items_v2.create({
            userId: currentUserId,
            productId: item._id,
            productName: item.name,
            productNameEn: item.nameEn || '',
            price: item.price,
            image: item.image,
            category: item.category,
            quantity: item.quantity || 1,
            maxQuantity: item.maxQuantity || 999,
            specifications: item.specifications || {},
            creator: currentUserId,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          })
        }
      } catch (error) {
        console.error('Failed to sync addItem to database:', error)
      }
    }
  }

  const removeItem = async (id: string) => {
    dispatch({ type: 'REMOVE_ITEM', payload: id })
    
    // 登入狀態下同步刪除資料庫記錄
    if (currentUserId) {
      try {
        const { list: existingItems } = await lumi.entities.cart_items_v2.list({
          filter: { userId: currentUserId, productId: id }
        })
        
        if (existingItems.length > 0) {
          await lumi.entities.cart_items_v2.delete(existingItems[0]._id)
        }
      } catch (error) {
        console.error('Failed to sync removeItem to database:', error)
      }
    }
  }

  const updateQuantity = async (id: string, quantity: number) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { id, quantity } })
    
    // 登入狀態下同步更新資料庫
    if (currentUserId) {
      try {
        const { list: existingItems } = await lumi.entities.cart_items_v2.list({
          filter: { userId: currentUserId, productId: id }
        })
        
        if (existingItems.length > 0) {
          if (quantity <= 0) {
            // 數量 <= 0，刪除記錄
            await lumi.entities.cart_items_v2.delete(existingItems[0]._id)
          } else {
            // 更新數量
            await lumi.entities.cart_items_v2.update(existingItems[0]._id, {
              quantity: Math.min(quantity, existingItems[0].maxQuantity || 999),
              updatedAt: new Date().toISOString()
            })
          }
        }
      } catch (error) {
        console.error('Failed to sync updateQuantity to database:', error)
      }
    }
  }

  const clearCart = async () => {
    dispatch({ type: 'CLEAR_CART' })
    
    // 如果登入状态，删除数据库资料
    const user = lumi.auth.user
    if (user?.userId) {
      try {
        const { list: existingItems } = await lumi.entities.cart_items_v2.list({
          filter: { userId: user.userId }
        })
        
        if (existingItems.length > 0) {
          await lumi.entities.cart_items_v2.deleteMany(
            existingItems.map((item: any) => item._id)
          )
        }
      } catch (error) {
        console.error('Failed to clear cart from database:', error)
      }
    }
  }

  return (
    <CartContext.Provider value={{
      ...state,
      addItem,
      removeItem,
      updateQuantity,
      clearCart
    }}>
      {children}
    </CartContext.Provider>
  )
}

export const useCart = (): CartContextType => {
  const context = useContext(CartContext)
  if (!context) {
    throw new Error('useCart must be used within a CartProvider')
  }
  return context
}
